// src/pages/admin/AdminEvents.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllEvents, deleteEvent, approveEvent, rejectEvent } from '../../lib/db';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';
import { useAuth } from '../../lib/AuthContext';

const CATEGORY_LABELS = {
  music: 'Music & Dance', sport_gaa: 'Sport — GAA', sport: 'Sport — Other',
  kids: 'Kids & Family', community: 'Community', markets: 'Markets',
  arts: 'Arts & Culture', health: 'Health & Wellbeing', farming: 'Farming & Equine',
  food: 'Food & Drink', education: 'Education & Workshops', social: 'Social',
};

const AdminEvents = () => {
  const navigate = useNavigate();
  const { isTownAdmin, townAdminScope } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [townFilter, setTownFilter] = useState(isTownAdmin ? townAdminScope : 'all');

  const load = async () => { setEvents(await getAllEvents()); setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteEvent(id);
    toast.success('Deleted');
    load();
  };

  const handleApprove = async (id) => {
    await approveEvent(id);
    toast.success('Approved — now live');
    load();
  };

  const handleReject = async (id) => {
    await rejectEvent(id);
    toast.success('Declined — hidden from public view');
    load();
  };

  const handleHide = async (id) => {
    await rejectEvent(id);
    toast.success('Hidden from public view');
    load();
  };

  const handleShow = async (id) => {
    await approveEvent(id);
    toast.success('Now live again');
    load();
  };

  const fmtDate = (date) => {
    if (!date) return '—';
    try { return format(date?.toDate ? date.toDate() : new Date(date), 'd MMM yyyy'); }
    catch { return '—'; }
  };

  const towns = [...new Set(events.map(ev => ev.townId).filter(Boolean))];
  const pendingCount = events.filter(ev => ev.status === 'pending_review').length;

  const filtered = events.filter(ev =>
    (!search || ev.title?.toLowerCase().includes(search.toLowerCase()) ||
      ev.organiserName?.toLowerCase().includes(search.toLowerCase())) &&
    (statusFilter === 'all' || (ev.status || 'published') === statusFilter) &&
    (categoryFilter === 'all' || ev.category === categoryFilter) &&
    (townFilter === 'all' || ev.townId === townFilter)
  );

  // Pending review items surface first regardless of other sort
  const sorted = [...filtered].sort((a, b) => {
    const aPending = a.status === 'pending_review' ? 0 : 1;
    const bPending = b.status === 'pending_review' ? 0 : 1;
    return aPending - bPending;
  });

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>{isTownAdmin ? `Events (${townAdminScope})` : `All Events (${events.length})`}</div>

      {isTownAdmin && (
        <div style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 13, borderRadius: 12, padding: '10px 14px', marginBottom: 16 }}>
          📍 Managing: {townAdminScope}
        </div>
      )}

      {pendingCount > 0 && (
        <div style={s.pendingBanner}>
          🔔 {pendingCount} event{pendingCount !== 1 ? 's' : ''} waiting for review
        </div>
      )}

      <input
        style={s.search}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by title or organiser..."
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="pending_review">Pending review</option>
          <option value="hidden">Hidden</option>
        </select>
        <select style={s.filterSelect} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([val, lbl]) => (
            <option key={val} value={val}>{lbl}</option>
          ))}
        </select>
        {!isTownAdmin && towns.length > 1 && (
          <select style={s.filterSelect} value={townFilter} onChange={e => setTownFilter(e.target.value)}>
            <option value="all">All towns</option>
            {towns.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {loading
        ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sorted.map(ev => {
              const isPending = ev.status === 'pending_review';
              return (
                <div key={ev.id} style={{ ...s.card, ...(isPending ? s.cardPending : {}) }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={s.cardTitle}>{ev.title}</div>
                    <span style={{
                      ...s.badge,
                      background: ev.status === 'published' ? 'var(--sage-pale)' : isPending ? '#FFF3CD' : '#F0F0EE',
                      color: ev.status === 'published' ? 'var(--sage-deep)' : isPending ? '#8A6D1D' : 'var(--muted)',
                    }}>
                      {isPending ? 'Pending review' : (ev.status || 'published')}
                    </span>
                  </div>
                  <div style={s.cardSub}>
                    {fmtDate(ev.nextDate || ev.date)} · {ev.townId} · by {ev.organiserName}
                  </div>
                  {isPending && ev.pendingReason && (
                    <div style={s.pendingReason}>
                      Flagged: {ev.pendingReason === 'keyword_flagged' ? `keyword match (${ev.flaggedKeyword || 'n/a'})`
                        : ev.pendingReason === 'profanity_flagged' ? 'profanity filter'
                        : ev.pendingReason === 'ai_flagged' ? (ev.aiModerationNote || 'AI review flag')
                        : ev.pendingReason}
                    </div>
                  )}
                  <div style={s.btnRow}>
                    {isPending && (
                      <>
                        <button onClick={() => handleApprove(ev.id)} style={s.approveBtn}>Approve</button>
                        <button onClick={() => handleReject(ev.id)} style={s.rejectBtn}>Decline</button>
                      </>
                    )}
                    {!isPending && ev.status === 'published' && (
                      <button onClick={() => handleHide(ev.id)} style={s.rejectBtn}>Hide</button>
                    )}
                    {!isPending && ev.status === 'hidden' && (
                      <button onClick={() => handleShow(ev.id)} style={s.approveBtn}>Show</button>
                    )}
                    <button onClick={() => navigate(`/organiser/edit/${ev.id}`)} style={s.editBtn}>Edit</button>
                    <button onClick={() => handleDelete(ev.id, ev.title)} style={s.deleteBtn}>Delete</button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No events found</div>}
          </div>
      }
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 },
  pendingBanner: { background: '#FFF3CD', color: '#8A6D1D', fontSize: 13, fontWeight: 700, borderRadius: 12, padding: '10px 14px', marginBottom: 14 },
  search: { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9', fontSize: 14, fontFamily: 'Nunito', marginBottom: 12, boxSizing: 'border-box', background: 'var(--white)' },
  filterSelect: { flex: 1, minWidth: 110, padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)' },
  card: { background: 'var(--white)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' },
  cardPending: { border: '1.5px solid #FFE39A' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ink)', flex: 1 },
  cardSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 6 },
  pendingReason: { fontSize: 11.5, color: '#8A6D1D', fontWeight: 600, marginBottom: 10, background: '#FFF9EB', borderRadius: 8, padding: '6px 10px' },
  badge: { fontSize: 10, padding: '2px 8px', borderRadius: 100, fontWeight: 700, flexShrink: 0, marginLeft: 8 },
  btnRow: { display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  approveBtn: { flex: 1, minWidth: 80, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
  rejectBtn: { flex: 1, minWidth: 80, background: '#FBEAE7', color: '#C0392B', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
  editBtn: { flex: 1, minWidth: 70, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
  deleteBtn: { background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
};

export default AdminEvents;
